import React from 'react';
import { Tooltip } from '@/Components/ui/Tooltip';

export interface CardSelectOption<T = string> {
  label: string;
  value: T;
  description?: string;
  icon?: React.ReactNode;
}

interface CardSelectInputProps<T = string> {
  options: CardSelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  name: string;
  label?: string;
  required?: boolean;
  className?: string;
  columns?: 2 | 3;
  tooltip?: string;
}

/**
 * Composant de sélection avec grandes cards design
 * Cards avec fond dégradé violet pour l'état sélectionné
 */
export const CardSelectInput = <T extends string | number = string>({
  options,
  value,
  onChange,
  name,
  label,
  required = false,
  className = '',
  columns = 3,
  tooltip,
}: CardSelectInputProps<T>) => {
  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-700">
          {label}
          {!required && <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1">(Optionnel)</span>}
          {tooltip && <Tooltip content={tooltip} />}
        </label>
      )}

      <div className={`tw-grid ${columns === 2 ? 'tw-grid-cols-1 sm:tw-grid-cols-2' : 'tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3'} tw-gap-3 sm:tw-gap-4`}>
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <label
              key={String(option.value)}
              className={`
                tw-flex tw-flex-col tw-p-3 sm:tw-p-5 tw-rounded-xl tw-cursor-pointer
                tw-transition-all tw-duration-200 tw-border-2
                ${isSelected
                  ? 'tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7] tw-border-white tw-shadow-lg tw-scale-105'
                  : 'tw-bg-white tw-border-gray-300 hover:tw-border-gray-400 hover:tw-shadow-md'
                }
              `}
            >
              <input
                type="radio"
                name={name}
                value={String(option.value)}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="tw-sr-only"
              />

              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                {/* Rond de sélection */}
                <div className={`
                  tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0
                  ${isSelected ? 'tw-border-white' : 'tw-border-gray-400'}
                `}>
                  {isSelected && (
                    <div className="tw-w-2.5 tw-h-2.5 tw-rounded-full tw-bg-white" />
                  )}
                </div>
                {option.icon && (
                  <div className={`tw-flex-shrink-0 ${isSelected ? 'tw-text-white' : 'tw-text-gray-500'}`}>
                    {option.icon}
                  </div>
                )}
                <span className={`tw-font-bold tw-text-base ${isSelected ? 'tw-text-white' : 'tw-text-gray-900'}`}>
                  {option.label}
                </span>
              </div>

              {option.description && (
                <p className={`tw-text-sm tw-mt-1 tw-ml-8 ${isSelected ? 'tw-text-white tw-opacity-90' : 'tw-text-gray-600'}`}>
                  {option.description}
                </p>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};
